import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import NudgeCard from './common/NudgeCard';
import PwaInstallInstructionsModal from './PwaInstallInstructionsModal';
import usePwaInstall from '../hooks/usePwaInstall';
import useBottomNavOffset from '../hooks/useBottomNavOffset';
import { useImpersonation } from '../context/ImpersonationContext';
import { useMessageSlot } from '../services/messageQueue';
import { MESSAGE_PRIORITY } from '../utils/messagePriorities';

// Transient/admin routes where promoting the app would be noise
const HIDDEN_PATHS = ['/admin/access', '/magiclink/redirect'];

const PwaInstallBanner = () => {
  const location = useLocation();
  const bottomNavOffset = useBottomNavOffset();
  const { isBannerVisible, isIos, isIosSafari, canPromptInstall, promptInstall, dismiss } = usePwaInstall();
  const { isImpersonating } = useImpersonation();
  const [showIosInstructions, setShowIosInstructions] = useState(false);

  // Admins impersonating a member shouldn't be nudged to install the app —
  // and the impersonation banner occupies the same bottom edge anyway
  const isHiddenPath = HIDDEN_PATHS.some((p) => location.pathname.startsWith(p));
  // Android without a captured install prompt has nothing actionable to offer
  const eligible = isBannerVisible && !isHiddenPath && !isImpersonating && (isIos || canPromptInstall);

  // Lowest-priority nudge: it shares the bottom edge with the "Your Box"
  // coach mark and the offer toasts, so it waits until those have had their
  // turn rather than sliding in on top of them.
  const { active } = useMessageSlot({
    id: 'pwa-install',
    priority: MESSAGE_PRIORITY.pwaInstall,
    ready: eligible,
  });

  if (!eligible || !active) return null;

  return (
    <>
      <NudgeCard
        layout="floating"
        eyebrow="One-tap access"
        title="Get the Wildgrain app"
        description="Add the member portal to your home screen for one-tap access."
        imageUrl="/wildgrain_logo_192.png"
        imageAlt=""
        primaryLabel={isIos ? 'Show me how' : 'Install app'}
        onPrimary={isIos ? () => setShowIosInstructions(true) : promptInstall}
        onSecondary={dismiss}
        onClose={dismiss}
        closeLabel="Dismiss install banner"
        className="md:hidden"
        style={{ bottom: `${bottomNavOffset + 12}px` }}
      />

      <PwaInstallInstructionsModal
        isOpen={showIosInstructions}
        onClose={() => setShowIosInstructions(false)}
        onGotIt={() => {
          setShowIosInstructions(false);
          dismiss();
        }}
        isIosSafari={isIosSafari}
      />
    </>
  );
};

export default PwaInstallBanner;
